import { useCallback, useRef } from 'react';
import axios from 'axios';
import s3Client from 'lib/s3Client';
import { uploadService, notification, offlineModeService } from 'utils/services';
import { findErrorDetail, sanitizeFileName, validateFileName } from 'utils/custom';

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

const PROGRESS_BAR_CLASS = 's3-upload-progress';
const UPLOADING_CLASS = 's3-uploading';

const setNavigationDisabled = (disabled) => {
  const surveyNav = document.querySelector('.sv_nav');
  if (!surveyNav) return;
  if (disabled) {
    surveyNav.classList.add(UPLOADING_CLASS);
    surveyNav.querySelectorAll('input[type="button"], button').forEach((btn) => {
      btn.disabled = true;
      btn.style.opacity = '0.5';
      btn.style.pointerEvents = 'none';
    });
  } else {
    surveyNav.classList.remove(UPLOADING_CLASS);
    surveyNav.querySelectorAll('input[type="button"], button').forEach((btn) => {
      btn.disabled = false;
      btn.style.opacity = '';
      btn.style.pointerEvents = '';
    });
  }
};

const createProgressBarHTML = (questionName) =>
  `<div class="${PROGRESS_BAR_CLASS}" data-question="${questionName}" style="width:100%;max-width:400px;margin-top:8px;">
    <div style="display:flex;align-items:center;gap:8px;">
      <div style="flex:1;height:20px;background-color:#e0e0e0;border-radius:8px;overflow:hidden;position:relative;">
        <div class="s3-upload-filler" style="height:100%;width:0%;background-color:#ff6b00;transition:width 0.3s ease-in-out;"></div>
        <span class="s3-upload-percent" style="position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);color:#000;font-size:12px;font-weight:bold;">0%</span>
      </div>
      <button class="s3-upload-cancel" type="button" style="display:flex;align-items:center;justify-content:center;width:20px;min-width:20px;max-width:20px;height:20px;padding:0;border:none;background:none;cursor:pointer;font-size:16px;color:#666;flex-shrink:0;" title="Cancel upload">✕</button>
    </div>
  </div>`;

const updateProgressBar = (questionName, percent) => {
  const bar = document.querySelector(`.${PROGRESS_BAR_CLASS}[data-question="${questionName}"]`);
  if (!bar) return;
  const filler = bar.querySelector('.s3-upload-filler');
  const text = bar.querySelector('.s3-upload-percent');
  if (filler) filler.style.width = `${percent}%`;
  if (text) text.textContent = `${percent}%`;
};

const removeProgressBar = (questionName) => {
  const bar = document.querySelector(`.${PROGRESS_BAR_CLASS}[data-question="${questionName}"]`);
  if (bar) bar.remove();
};

const getQuestionEl = (question) => {
  if (!question) return null;
  return document.getElementById(question.id);
};

const showError = (question, questionName, message) => {
  removeProgressBar(questionName);
  const questionEl = getQuestionEl(question);
  if (!questionEl) return;
  const errorEl = document.createElement('div');
  errorEl.className = `${PROGRESS_BAR_CLASS}`;
  errorEl.setAttribute('data-question', questionName);
  errorEl.style.cssText = 'color:red;font-size:12px;margin-top:4px;';
  errorEl.textContent = message;
  questionEl.appendChild(errorEl);
};

const PENDING_UPLOAD_MARKER = '__pending_upload__';

const useSurveyS3Upload = () => {
  const cancelTokensRef = useRef({});
  const uploadingCountRef = useRef(0);
  const pendingFilesRef = useRef({});
  const previewUrlsRef = useRef({});

  const getUploadingCount = useCallback(() => uploadingCountRef.current, []);
  const getPendingFiles = useCallback(() => ({ ...pendingFilesRef.current }), []);

  const handleUploadFiles = useCallback((sender, options) => {
    const { name: questionName, question, files } = options;
    const rawFile = files[0];

    if (!rawFile) {
      options.callback('error', []);
      return;
    }

    if (rawFile.size > MAX_FILE_SIZE) {
      showError(
        question,
        questionName,
        `File size exceeds 10MB limit (${(rawFile.size / (1024 * 1024)).toFixed(1)}MB)`,
      );
      options.callback('error', []);
      return;
    }

    const sanitizedName = sanitizeFileName(rawFile.name);
    const file = sanitizedName === rawFile.name ? rawFile : new File([rawFile], sanitizedName, { type: rawFile.type });

    const fileNameError = validateFileName(file.name);
    if (fileNameError) {
      showError(question, questionName, fileNameError);
      options.callback('error', []);
      return;
    }

    if (!navigator.onLine || offlineModeService.getCached()) {
      pendingFilesRef.current[questionName] = {
        blob: file,
        name: file.name,
        content_type: file.type,
      };

      const pendingMarker = {
        [PENDING_UPLOAD_MARKER]: true,
        name: file.name,
        content_type: file.type,
      };

      previewUrlsRef.current[questionName] = URL.createObjectURL(file);

      options.callback('success', [
        {
          file,
          content: JSON.stringify(pendingMarker),
          name: file.name,
        },
      ]);
      return;
    }

    removeProgressBar(questionName);
    const questionEl = getQuestionEl(question);
    if (questionEl) {
      questionEl.insertAdjacentHTML('beforeend', createProgressBarHTML(questionName));

      const cancelBtn = questionEl.querySelector(
        `.${PROGRESS_BAR_CLASS}[data-question="${questionName}"] .s3-upload-cancel`,
      );
      if (cancelBtn) {
        cancelBtn.addEventListener('click', () => {
          const token = cancelTokensRef.current[questionName];
          if (token) {
            token.cancel('Upload cancelled by user');
            delete cancelTokensRef.current[questionName];
          }
          uploadingCountRef.current = Math.max(0, uploadingCountRef.current - 1);
          removeProgressBar(questionName);
          options.callback('error', []);
        });
      }
    }

    uploadingCountRef.current += 1;
    setNavigationDisabled(true);

    (async () => {
      let stage = 'presign';
      try {
        const presignedResponse = await uploadService.getPresignedUrls([
          { filename: file.name, content_type: file.type },
        ]);

        const presignedFiles = presignedResponse.data?.files || presignedResponse.files;
        const presignedFile = presignedFiles?.[0];
        if (!presignedFile?.upload_url) {
          stage = 'invalid_response';
          throw new Error('Invalid presigned URL response');
        }

        stage = 's3';
        const cancelToken = axios.CancelToken.source();
        cancelTokensRef.current[questionName] = cancelToken;

        await s3Client.put(presignedFile.upload_url, file, {
          headers: { 'Content-Type': file.type },
          cancelToken: cancelToken.token,
          onUploadProgress: (progressEvent) => {
            const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            updateProgressBar(questionName, percent);
          },
        });

        delete cancelTokensRef.current[questionName];
        uploadingCountRef.current = Math.max(0, uploadingCountRef.current - 1);
        if (uploadingCountRef.current === 0) setNavigationDisabled(false);
        removeProgressBar(questionName);

        const fileData = uploadService.buildFileData(presignedFile, file);
        previewUrlsRef.current[questionName] = URL.createObjectURL(file);

        options.callback('success', [
          {
            file,
            content: JSON.stringify(fileData),
            name: file.name,
          },
        ]);
      } catch (err) {
        delete cancelTokensRef.current[questionName];
        uploadingCountRef.current = Math.max(0, uploadingCountRef.current - 1);
        if (uploadingCountRef.current === 0) setNavigationDisabled(false);

        if (axios.isCancel(err)) return;

        const status = err.response?.status;
        const raw = err.response?.data;
        let errorMsg;

        if (stage === 'invalid_response') {
          errorMsg = 'Server returned an invalid upload URL. Please try again.';
        } else if (stage === 'presign') {
          if (status === 401) {
            errorMsg = 'Authorization failed. Please log in again.';
          } else if (status >= 500) {
            errorMsg = 'Upload service is not available. Please try again later.';
          } else if (raw) {
            const detail = findErrorDetail(raw);
            errorMsg = detail || 'Could not start upload. Please try again.';
          } else {
            errorMsg = 'Could not reach upload service. Please check your connection and try again.';
          }
        } else if (status === 403) {
          errorMsg = 'Upload URL expired. Please try again.';
        } else if (status === 400) {
          const detail = findErrorDetail(raw);
          errorMsg = detail || 'Invalid file. Please check the file type and try again.';
        } else if (status >= 500) {
          errorMsg = 'Storage service is not available. Please try again later.';
        } else if (raw) {
          const detail = findErrorDetail(raw);
          errorMsg = detail || 'Upload failed. Please try again.';
        } else {
          errorMsg = 'Could not upload to storage. Please check your connection and try again.';
        }

        const displayMsg = Array.isArray(errorMsg) ? errorMsg.join(', ') : errorMsg;
        notification.error(displayMsg);
        showError(question, questionName, displayMsg);
        options.callback('error', []);
      }
    })();
  }, []);

  const handleDownloadFile = useCallback((sender, options) => {
    const previewUrl = previewUrlsRef.current[options.name];
    if (previewUrl) {
      options.callback('success', previewUrl);
      return;
    }
    options.callback('success', options.content);
  }, []);

  const handleClearFiles = useCallback((sender, options) => {
    const { name: questionName } = options;
    delete pendingFilesRef.current[questionName];
    if (previewUrlsRef.current[questionName]) {
      URL.revokeObjectURL(previewUrlsRef.current[questionName]);
      delete previewUrlsRef.current[questionName];
    }
    const token = cancelTokensRef.current[questionName];
    if (token) {
      token.cancel('File cleared');
      delete cancelTokensRef.current[questionName];
      uploadingCountRef.current = Math.max(0, uploadingCountRef.current - 1);
    }
    removeProgressBar(questionName);
    options.callback('success');
  }, []);

  const cancelAll = useCallback(() => {
    Object.keys(cancelTokensRef.current).forEach((questionName) => {
      const token = cancelTokensRef.current[questionName];
      if (token) token.cancel('All uploads cancelled');
      removeProgressBar(questionName);
    });
    Object.values(previewUrlsRef.current).forEach((url) => URL.revokeObjectURL(url));
    cancelTokensRef.current = {};
    uploadingCountRef.current = 0;
    pendingFilesRef.current = {};
    previewUrlsRef.current = {};
  }, []);

  const configureFileQuestions = useCallback((surveyModel) => {
    if (!surveyModel) return;
    surveyModel.getAllQuestions().forEach((question) => {
      if (question.getType() === 'file') {
        question.storeDataAsText = false;
        question.waitForUpload = true;
        question.maxSize = MAX_FILE_SIZE;
      }
    });
  }, []);

  return {
    handleUploadFiles,
    handleDownloadFile,
    handleClearFiles,
    cancelAll,
    getUploadingCount,
    getPendingFiles,
    configureFileQuestions,
  };
};

export { PENDING_UPLOAD_MARKER };
export default useSurveyS3Upload;
